import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api, wsUrl, TABS } from "@/lib/api";
import { LiveCard } from "@/components/LiveCard";
import { InstitutionList } from "@/components/InstitutionList";
import { InstitutionShorts } from "@/components/shorts/InstitutionShorts";
import { Skeleton } from "@/components/ui/skeleton";

function useLiveCounts() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let ws;
    let stop = false;
    const connect = () => {
      ws = new WebSocket(wsUrl("/ws/feed"));
      ws.onmessage = (e) => {
        const d = JSON.parse(e.data);
        if (d.type === "counts") setCounts(d.counts);
      };
      ws.onclose = () => !stop && setTimeout(connect, 3000);
    };
    connect();
    return () => {
      stop = true;
      ws?.close();
    };
  }, []);
  return counts;
}

export default function Feed() {
  const [tab, setTab] = useState("all");
  const [following, setFollowing] = useState(false);
  const [inst, setInst] = useState(null);
  const [rooms, setRooms] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const counts = useLiveCounts();

  useEffect(() => {
    if (tab === "institutions" && !inst) {
      api.get("/institutions").then((r) => setInstitutions(r.data));
      return;
    }
    setRooms(null);
    const params = inst ? { institution_id: inst.id } : { category: tab, ...(following ? { following: true } : {}) };
    api.get("/rooms", { params }).then((r) => setRooms(r.data)).catch(() => setRooms([]));
  }, [tab, inst, following]);

  const pickTab = (k) => {
    setInst(null);
    setTab(k);
  };

  return (
    <div className="pt-4 md:pt-8">
      <div className="sticky top-16 z-30 -mx-3 md:mx-0 glass md:bg-transparent md:backdrop-blur-none px-3 md:px-0 py-2">
        <div className="mb-2 flex items-center gap-5" data-testid="lives-mode-tabs">
          {[[false, "Para você", "lives-tab-foryou"], [true, "Acompanhando", "lives-tab-following"]].map(([on, label, testid]) => (
            <button key={label} onClick={() => { setInst(null); setFollowing(on); if (tab === "institutions") setTab("all"); }} data-testid={testid}
              className={`relative pb-1 text-base font-extrabold transition-colors ${following === on ? "text-white" : "text-white/50 hover:text-white/80"}`}>
              {label}
              <span className={`absolute inset-x-0 -bottom-0.5 h-0.5 rounded-full bg-[hsl(var(--gold))] transition-transform ${following === on ? "scale-x-100" : "scale-x-0"}`} />
            </button>
          ))}
        </div>
        <div className="no-scrollbar flex gap-2 overflow-x-auto" data-testid="feed-tabs">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => pickTab(t.key)} data-testid={t.testid}
              className={`shrink-0 rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === t.key ? "bg-white text-[#0B0F17]" : "bg-white/5 text-muted-foreground hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {inst && (
        <div className="mt-4 flex items-center gap-2 text-sm" data-testid="institution-filter">
          <span className="text-muted-foreground">Salas de</span>
          <span className="font-bold">{inst.name}</span>
          <button onClick={() => setInst(null)} className="grid h-7 w-7 place-items-center rounded-full bg-white/10" data-testid="clear-institution-filter"><X size={14} /></button>
        </div>
      )}
      <div className={`mt-4 pb-10 ${inst ? "grid gap-8 lg:grid-cols-[1fr_400px]" : ""}`}>
        <section data-testid={inst ? "institution-lives-section" : undefined}>
        {inst && <h2 className="mb-3 text-lg font-extrabold">Lives</h2>}
        {tab === "institutions" && !inst ? (
          <InstitutionList items={institutions} onPick={setInst} />
        ) : rooms === null ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-4">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-2xl" />)}
          </div>
        ) : rooms.length === 0 ? (
          <p className="py-20 text-center text-muted-foreground" data-testid="feed-empty">{following ? "Nenhuma igreja que você acompanha está ao vivo agora." : "Nenhuma sala ao vivo nesta categoria agora."}</p>
        ) : (
          <div className={`grid grid-cols-2 md:grid-cols-3 ${inst ? "" : "lg:grid-cols-4"} gap-2.5 md:gap-4`} data-testid="live-grid">
            {rooms.map((r, i) => <LiveCard key={r.id} room={r} count={counts[r.id]} index={i} />)}
          </div>
        )}
        </section>
        {inst && <InstitutionShorts institutionId={inst.id} />}
      </div>
    </div>
  );
}
